// Routes Sidebar Component - Left Panel

import React from 'react';
import Splitter, { Item } from 'devextreme-react/splitter';
import { getVehicleColor } from './map-utils';

interface RoutesSidebarProps {
  routes: any[];
  selectedRoute: any | null;
  onRouteClick: (route: any) => void;
  onCloseRoute?: () => void;
  onCustomerClick?: (customer: any) => void;
}

export const RoutesSidebar: React.FC<RoutesSidebarProps> = ({
  routes,
  selectedRoute,
  onRouteClick,
  onCloseRoute,
  onCustomerClick
}) => {
  // Helper function to truncate time to HH:MM format
  const formatTime = (time: string | undefined) => {
    if (!time) return '-';
    const parts = time.split(':');
    if (parts.length >= 2) {
      return `${parts[0]}:${parts[1]}`;
    }
    return time;
  };

  return (
    <div className={`routes-sidebar ${selectedRoute ? 'with-customer-panel' : 'routes-only'}`}>
      {selectedRoute ? (
        <Splitter orientation="horizontal">
          {/* Routes List Panel */}
          <Item size="100px" minSize="100px" maxSize="200px">
            <div className="routes-panel routes-list-panel">
        <div className="routes-panel-header">
          <span>🗺️ Rotalar</span>
          <span className="routes-count">{routes.length}</span>
        </div>
        <div className="routes-panel-content">
          {routes.map((route) => {
            // Extract vehicle number from vehicle_id (e.g., "vehicle_1" -> 1)
            const vehicleIdNum = typeof route.vehicle_id === 'string'
              ? parseInt(route.vehicle_id.replace('vehicle_', ''))
              : route.vehicle_id;
            const vehicleColor = getVehicleColor(vehicleIdNum);
            const isSelected = selectedRoute?.vehicle_id === route.vehicle_id;

            return (
              <div
                key={route.vehicle_id}
                className={`route-card ${isSelected ? 'selected' : ''}`}
                style={{ '--route-color': vehicleColor } as React.CSSProperties}
                onClick={() => onRouteClick(route)}
              >
                <div className="route-card-icon" style={{ backgroundColor: vehicleColor }}>
                  <i className="pi pi-truck"></i>
                </div>
                <div className="route-card-info">
                  <div className="route-card-title">
                    🚚 {route.vehicle_type || `Araç ${route.vehicle_id}`}
                  </div>
                  <div className="route-card-stats">
                    <span>
                      👥 {route.customers_count || 0}
                    </span>
                    <span>
                      📍 {(route.distance || 0).toFixed(1)} km
                    </span>
                    <span>
                      ⏱️ {Math.round((route.route_time || 0) / 60)} dk
                    </span>
                  </div>
                  {(route.start_time_formatted || route.end_time_formatted) && (
                    <div className="route-card-time" style={{
                      marginTop: '4px',
                      fontSize: '10px',
                      color: '#64748b',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        🟢 {formatTime(route.start_time_formatted)}
                      </span>
                      <span>→</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        🔴 {formatTime(route.end_time_formatted)}
                      </span>
                    </div>
                  )}
                </div>
                {isSelected && <i className="pi pi-check-circle route-selected-icon"></i>}
              </div>
            );
          })}
        </div>
            </div>
          </Item>

          {/* Customer Sequence Panel */}
          <Item>
            <div className="routes-panel">
          <div className="routes-panel-header">
            {/* Sol - X Butonu ve Başlık */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {onCloseRoute && (
                <button
                  onClick={onCloseRoute}
                  style={{
                    background: 'rgba(255, 255, 255, 0.15)',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    cursor: 'pointer',
                    padding: '2px 6px',
                    color: '#ffffff',
                    fontSize: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '4px',
                    transition: 'all 0.2s',
                    lineHeight: 1,
                    fontWeight: 'bold',
                    minWidth: '20px',
                    minHeight: '20px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#ef4444';
                    e.currentTarget.style.borderColor = '#ef4444';
                    e.currentTarget.style.transform = 'scale(1.05)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  title="Müşteri sıralamasını kapat"
                >
                  <i className="dx-icon dx-icon-close" style={{ fontSize: '10px' }}></i>
                </button>
              )}
              <span>📋 Müşteri Sıralaması</span>
            </div>
            {/* Sağ - Sayaç */}
            <span className="routes-count">
              {selectedRoute.route_sequence?.filter((s: any) => s.location_type === 'CUSTOMER' || s.location_type === 'customer').length || 0}
            </span>
          </div>
          <div className="routes-panel-content">
            <div className="customer-sequence-list">
              {selectedRoute.route_sequence?.map((step: any, index: number) => {
                const isFirst = index === 0;
                const isLast = index === selectedRoute.route_sequence.length - 1;
                const isDepot = step.location_type === 'DEPOT' || step.location_type === 'depot';
                const isCustomer = step.location_type === 'CUSTOMER' || step.location_type === 'customer';
                const isBreak = step.location_type === 'BREAK' || step.location_type === 'break';
                const nextStep = selectedRoute.route_sequence[index + 1];

                if (!isCustomer && !isDepot && !isBreak) return null;

                const customerSequence = selectedRoute.route_sequence
                  .slice(0, index)
                  .filter((s: any) => s.location_type === 'CUSTOMER' || s.location_type === 'customer')
                  .length + (isCustomer ? 1 : 0);

                return (
                  <React.Fragment key={step.customer_id || step.id || index}>
                    <div
                      className={`sequence-stop ${isDepot ? 'depot' : isBreak ? 'break' : 'customer'} ${isCustomer && onCustomerClick ? 'clickable' : ''}`}
                      onClick={() => isCustomer && onCustomerClick && onCustomerClick(step)}
                      style={isCustomer && onCustomerClick ? { cursor: 'pointer' } : undefined}
                    >
                      {/* Icon */}
                      <div className={`sequence-icon ${isDepot ? 'depot' : isBreak ? 'break' : 'customer'}`}>
                        {isDepot ? (
                          <span style={{ fontSize: '16px' }}>{isFirst ? '🏭' : '🏁'}</span>
                        ) : isBreak ? (
                          <span style={{ fontSize: '16px' }}>☕</span>
                        ) : (
                          customerSequence
                        )}
                      </div>

                      {/* Content */}
                      <div className="sequence-content">
                        {/* Header with name */}
                        <div className="sequence-header">
                          <div className="sequence-name" title={step.customer_name || step.name}>
                            {isBreak ? (
                              <span>☕ Mola</span>
                            ) : (
                              step.customer_name || step.name || (isDepot ? (isFirst ? '🏭 Başlangıç' : '🏁 Bitiş') : `Müşteri ${customerSequence}`)
                            )}
                          </div>
                        </div>

                        {/* Time */}
                        <div className="sequence-time-row">
                          <span>🕐 {formatTime(step.arrival_time_formatted)}</span>
                          {step.end_time_formatted && (
                            <>
                              <span>→</span>
                              <span className="sequence-time-end">🏁 {formatTime(step.end_time_formatted)}</span>
                            </>
                          )}
                        </div>

                        {/* Info Pills */}
                        <div className="sequence-info">
                          {isBreak && step.duration && (
                            <div className="sequence-pill service" title="Mola Süresi">
                              ⏱️ {typeof step.duration === 'number' ? `${Math.round(step.duration / 60)} dk` : step.duration}
                            </div>
                          )}
                          {isCustomer && step.time_window && (
                            <div className="sequence-pill tw" title="Zaman Penceresi">
                              📅 {step.time_window.start}-{step.time_window.end}
                            </div>
                          )}
                          {isCustomer && step.demand && (
                            <div className="sequence-pill demand" title="Talep">
                              📦 {step.demand}kg
                            </div>
                          )}
                          {step.service_duration_formatted && step.service_duration_formatted !== '00:00:00' && !isBreak && (
                            <div className="sequence-pill service" title="Servis Süresi">
                              🔧 {(() => {
                                const parts = step.service_duration_formatted.split(':');
                                const hours = parseInt(parts[0] || 0);
                                const minutes = parseInt(parts[1] || 0);
                                const totalMinutes = hours * 60 + minutes;
                                return totalMinutes > 0 ? `${totalMinutes} dk` : step.service_duration_formatted;
                              })()}
                            </div>
                          )}
                          {step.waiting_time_formatted && step.waiting_time_formatted !== '00:00:00' && (
                            <div className="sequence-pill wait" title="Bekleme">
                              ⏳ {(() => {
                                const parts = step.waiting_time_formatted.split(':');
                                const hours = parseInt(parts[0] || 0);
                                const minutes = parseInt(parts[1] || 0);
                                const totalMinutes = hours * 60 + minutes;
                                return totalMinutes > 0 ? `${totalMinutes} dk` : step.waiting_time_formatted;
                              })()}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Distance Connector - Between customers */}
                    {!isLast && nextStep?.valhalla_distance_km && (
                      <div className="sequence-distance-connector">
                        <div className="connector-line"></div>
                        <div className="connector-badge">
                          <span>{nextStep.valhalla_distance_km.toFixed(1)} km</span>
                          {nextStep.valhalla_time_formatted && (
                            <span style={{ marginLeft: '6px', fontSize: '10px', opacity: 0.8 }}>
                              {formatTime(nextStep.valhalla_time_formatted)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {!isLast && !nextStep?.valhalla_distance_km && (
                      <div className="sequence-line"></div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
            </div>
          </Item>
        </Splitter>
      ) : (
        // No route selected - show only routes list
        <div className="routes-panel routes-list-panel">
          <div className="routes-panel-header">
            <span>🗺️ Rotalar</span>
            <span className="routes-count">{routes.length}</span>
          </div>
          <div className="routes-panel-content">
            {routes.map((route) => {
              const vehicleIdNum = typeof route.vehicle_id === 'string'
                ? parseInt(route.vehicle_id.replace('vehicle_', ''))
                : route.vehicle_id;
              const vehicleColor = getVehicleColor(vehicleIdNum);
              const isSelected = selectedRoute?.vehicle_id === route.vehicle_id;

              return (
                <div
                  key={route.vehicle_id}
                  className={`route-card ${isSelected ? 'selected' : ''}`}
                  style={{ '--route-color': vehicleColor } as React.CSSProperties}
                  onClick={() => onRouteClick(route)}
                >
                  <div className="route-card-icon" style={{ backgroundColor: vehicleColor }}>
                    <i className="pi pi-truck"></i>
                  </div>
                  <div className="route-card-info">
                    <div className="route-card-title">
                      🚚 {route.vehicle_type || `Araç ${route.vehicle_id}`}
                    </div>
                    <div className="route-card-stats">
                      <span>
                        👥 {route.customers_count || 0}
                      </span>
                      <span>
                        📍 {(route.distance || 0).toFixed(1)} km
                      </span>
                      <span>
                        ⏱️ {Math.round((route.route_time || 0) / 60)} dk
                      </span>
                    </div>
                    {(route.start_time_formatted || route.end_time_formatted) && (
                      <div className="route-card-time" style={{
                        marginTop: '4px',
                        fontSize: '10px',
                        color: '#64748b',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          🟢 {formatTime(route.start_time_formatted)}
                        </span>
                        <span>→</span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          🔴 {formatTime(route.end_time_formatted)}
                        </span>
                      </div>
                    )}
                  </div>
                  {isSelected && <i className="pi pi-check-circle route-selected-icon"></i>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
